import {
  Heading,
  HStack,
  Icon,
  Show,
  Space,
  Text,
  ThemeColorPath,
  ThemeIconKey,
  VStack,
} from 'platform/foundation';
import {match} from 'ts-pattern';

import {always, isNotNil} from 'ramda';

import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {getMazdaAmountAgreementState} from '../utils/getMazdaAmountAgreementState';

interface MazdaDetailOverviewAmountCellProps extends TestIdProps {
  label: string;
  requestedAmount: number | Nullish;
  agreedAmount: number | Nullish;
  formatAmount: (amount: number | Nullish) => string | undefined;
}

export function MazdaDetailOverviewAmountCell(props: MazdaDetailOverviewAmountCellProps) {
  const agreementState = getMazdaAmountAgreementState(props.requestedAmount, props.agreedAmount);

  const icon = match<typeof agreementState, ThemeIconKey | undefined>(agreementState)
    .with('agreed', always('action/check_circle'))
    .with('notAgreed', always('alert/error'))
    .otherwise(always(undefined));

  const iconColor = match<typeof agreementState, ThemeColorPath | undefined>(agreementState)
    .with('agreed', always('severity.success'))
    .with('notAgreed', always('severity.danger'))
    .otherwise(always(undefined));

  return (
    <VStack spacing={1}>
      <Text size="xSmall" color="secondary">
        {props.label}
      </Text>
      <HStack align="center" spacing={1}>
        <Heading size={2} data-testid={suffixTestId('agreedAmount', props)}>
          {props.formatAmount(props.agreedAmount) ?? EMPTY_PLACEHOLDER}
        </Heading>
        <Show when={isNotNil(icon)}>
          <Icon size={4} value={icon} color={iconColor} />
        </Show>
      </HStack>
      <Space vertical={2} />
      <Text size="xSmall" color="secondary">
        {i18n.t('entity.orderIntegration.labels.requestedAmount')}
      </Text>
      <Text size="base" color="secondary" data-testid={suffixTestId('requestedAmount', props)}>
        {props.formatAmount(props.requestedAmount) ?? EMPTY_PLACEHOLDER}
      </Text>
    </VStack>
  );
}
